/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { logger as log } from '@common/logger';
import { vappContainer } from '@/application';
import { AppNavigatorService } from '@/AppNavigator';
import { Client, Provider, cacheExchange, fetchExchange } from 'urql';
import { Graphql } from '@common/graphql';
import { Sttp } from '@common/sttp';
import { StorageModule } from '@vlinder-web/storage-module-react';
import { toHeaderCase } from 'js-convert-case';
import { apiConfig } from '@pages/OrganizationProfile/config/api.config';
import { featureProfileConfig } from '@pages/OrganizationProfile/config/featuresSection.config';
import { GET_MY_PROFILE } from '@pages/OrganizationProfile/api/graphql';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};
const lookup = require('country-code-lookup');

export interface IFeatureProfilePluginMethods extends IPluginMethodMap {
  getFeatureProfile(args?: any): Promise<any>;
}

export class FeatureProfilePlugin implements IAgentPlugin {
  readonly methods: IFeatureProfilePluginMethods;

  constructor() {
    this.methods = {
      getFeatureProfile: this.getFeatureProfile.bind(this),
    };
  }

  async getFeatureProfile(args?: any) {
    await new Promise((r) => setTimeout(r, 2000));
    return await this.findCapsules(args);
    // return {
    //   feature:featureProfileConfig
    // }
  }

  //Graphql Query example
  async findCapsules(args?: any) {
    // try {
    let gql = Graphql.withBaseUrl(apiConfig.gql.url);
    const token = loginSrv()?.getToken();
    //const token = null;
    if (token) {
      gql = gql.withHeaders({
        authentication: token,
      });
    }

    const client = gql.getClient();
    const storageSrv = StorageModule?.getInstance();
    const myProfile = storageSrv.get('myProfile', 'session');
    if (myProfile?.email && args?.type !== 'TRIGGER_REFRESH') {
      let _res = myProfile;
      return {
        title: `${toHeaderCase(_res?.firstName ?? '')} ${toHeaderCase(
          _res?.lastName ?? ''
        )}`,
        subtitle: _res?.vuid ? `Account ID: ${_res?.vuid}` : _res?.occupation,
        heroImage: {
          image:
            _res?.image ??
            `https://ui-avatars.com/api/?name=${_res.firstName}+${_res?.lastName}&background=0D8ABC&color=fff`,
        },
        listItems: this.getListItems(_res),
        initialData: {
          firstName: _res?.firstName,
          lastName: _res?.lastName,
          image: _res?.image,
          telephone: _res?.primaryContactNo,
          address: _res?.address?.addressLine1,
          city: _res?.address?.city,
          country: _res?.address?.country,
          occupation: _res?.occupation,
        },
        buttons: featureProfileConfig?.buttons,
        organizationProfile: {
          title: _res?.org?.name,
          subtitle: _res?.org?.description,
          heroImage: {
            image:
              _res?.org?.logo ??
              `https://ui-avatars.com/api/?name=${_res.org?.name}&background=0D8ABC&color=fff`,
          },
          listItems: this.getOrgListItems(_res?.org),
          buttons: featureProfileConfig?.buttons,
          formDefaultData: {
            name: _res?.org?.name,
            description: _res?.org?.description,
            image: _res?.org?.logo,
            address: _res?.org?.address?.addressLine1,
            country: _res?.org?.address?.country,
            city: _res?.org?.address?.city,
            telephone: _res?.org?.primaryContactNo,
            website: _res?.org?.website,
          },
        },
      };
    } else {
      try {
        if (client) {
          const res = await client.query(GET_MY_PROFILE, {}).toPromise();
          if (res?.data?.getMyProfile) {
            storageSrv?.set('myProfile', res?.data?.getMyProfile, 'session');
            let _res = res?.data?.getMyProfile;
            return {
              title: `${toHeaderCase(_res?.firstName ?? '')} ${toHeaderCase(
                _res?.lastName ?? ''
              )}`,
              subtitle: _res?.vuid
                ? `Account ID: ${_res?.vuid}`
                : _res?.occupation,
              heroImage: {
                image:
                  _res?.image ??
                  `https://ui-avatars.com/api/?name=${_res.firstName}+${_res?.lastName}&background=0D8ABC&color=fff`,
              },
              listItems: this.getListItems(_res),
              initialData: {
                firstName: _res?.firstName,
                lastName: _res?.lastName,
                image: _res?.image,
                telephone: _res?.primaryContactNo,
                address: _res?.address?.addressLine1,
                city: _res?.address?.city,
                country: _res?.address?.country,
                occupation: _res?.occupation,
              },
              buttons: featureProfileConfig?.buttons,
              organizationProfile: {
                title: _res?.org?.name,
                subtitle: _res?.org?.description,
                heroImage: {
                  image:
                    _res?.org?.logo ??
                    `https://ui-avatars.com/api/?name=${_res.org?.name}&background=0D8ABC&color=fff`,
                },
                listItems: this.getOrgListItems(_res?.org),
                buttons: featureProfileConfig?.buttons,
                formDefaultData: {
                  name: _res?.org?.name,
                  description: _res?.org?.description,
                  image: _res?.org?.logo,
                  address: _res?.org?.address?.addressLine1,
                  country: _res?.org?.address?.country,
                  city: _res?.org?.address?.city,
                  telephone: _res?.org?.primaryContactNo,
                  website: _res?.org?.website,
                },
              },
            };
          }
        }
      } catch (err: any) {
        log.error('Sign In user error', err?.response?.data);
        throw err;
      }
    }

    // private formatProducts(products: any) {
    //   return products.map((product: any) => product);
    // }
  }
  private getOrgListItems(res: any) {
    let _temp = [];
    _temp = featureProfileConfig?.orglistItems?.map((item: any) => {
      if (item?.title === 'Address') {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : '-',
        };
      }
      if (item?.title === 'City') {
        return {
          ...item,
          subTitle: res?.address?.city ?? '-',
        };
      }

      if (item?.title === 'Country') {
        return {
          ...item,
          subTitle:
            res?.address?.country && res?.address?.country.length === 2
              ? lookup.byIso(`${res?.address?.country}`)?.country
              : '-',
          flag: res?.address?.country,
        };
      }
      if (item?.title === 'Email') {
        return {
          ...item,
          subTitle: res?.email ?? '-',
        };
      }
      if (item?.title === 'Phone Number') {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? '-',
        };
      }
      if (item?.title === 'Website') {
        return {
          ...item,
          subTitle: res?.website ?? '-',
        };
      }
    });
    return _temp;
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = featureProfileConfig?.listItems?.map((item) => {
      if (item?.title === 'Full Name') {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.firstName ?? '')} ${toHeaderCase(
            res?.lastName ?? ''
          )}`,
        };
      }
      if (item?.title === 'Street Address') {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : '-',
        };
      }
      if (item?.title === 'City') {
        return {
          ...item,
          subTitle: res?.address?.city ?? '-',
        };
      }
      if (item?.title === 'Country') {
        return {
          ...item,
          subTitle:
            res?.address?.country && res?.address?.country.length === 2
              ? lookup.byIso(`${res?.address?.country}`)?.country
              : '-',
          flag: res?.address?.country,
        };
      }
      if (item?.title === 'Email') {
        return {
          ...item,
          subTitle: res?.email ?? '-',
        };
      }
      if (item?.title === 'Telephone') {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? '-',
        };
      }
      if (item?.title === 'Occupation') {
        return {
          ...item,
          subTitle: res?.occupation ?? '-',
        };
      }
    });
    return _temp;
  }
}
